import { Box, Checkbox, FormControlLabel, FormGroup, Paper, Stack, Typography } from '@mui/material';
import { fetchRoles, updateMemberOrganization } from 'api/portal';
import Button from 'components/Button';
import Fade from 'components/Fade';
import SectionLoader from 'components/SectionLoader';
import Subtitle from 'components/Subtitle';
import { setActiveOrganizationData, setOrganizations } from 'features/userSlice';
import { showErrorToast, showSuccessToast } from 'features/toastSlice';
import App from 'pages/organization/settings/App';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

function mapRoleIds(obj) {
  return (obj || []).map((roleId) => Number(roleId));
}

function OrganizationSecurityPolicy() {
  const organization = useSelector((state) => state.user.active_organization);
  const organizations = useSelector((state) => state.user.organizations);

  const [roles, setRoles] = useState([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [enableTwoFactorPortal, setEnableTwoFactorPortal] = useState(Boolean(organization.enable_2fa_portal));
  const [enableTwoFactorApp, setEnableTwoFactorApp] = useState(Boolean(organization.enable_2fa_app));
  const [twoFactorRolesPortal, setTwoFactorRolesPortal] = useState(
    mapRoleIds(organization.enable_2fa_roles_portal),
  );
  const [twoFactorRolesApp, setTwoFactorRolesApp] = useState(mapRoleIds(organization.enable_2fa_roles_app));
  const [allow2faSkipConfiguration, setAllow2faSkipConfiguration] = useState(
    Boolean(organization.allow_2fa_skip_configuration),
  );
  const [errors, setErrors] = useState(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    setEnableTwoFactorPortal(Boolean(organization.enable_2fa_portal));
    setEnableTwoFactorApp(Boolean(organization.enable_2fa_app));
    setTwoFactorRolesPortal(mapRoleIds(organization.enable_2fa_roles_portal));
    setTwoFactorRolesApp(mapRoleIds(organization.enable_2fa_roles_app));
    setAllow2faSkipConfiguration(Boolean(organization.allow_2fa_skip_configuration));
  }, [
    organization.enable_2fa_app,
    organization.enable_2fa_portal,
    organization.enable_2fa_roles_portal,
    organization.enable_2fa_roles_app,
    organization.allow_2fa_skip_configuration,
  ]);

  useEffect(() => {
    fetchRoles()
      .then((response) => {
        const { data } = response;
        setRoles(data || []);
        setRolesLoaded(true);
      })
      .catch(() => {
        setRolesLoaded(true);
        dispatch(showErrorToast('messages.error-roles'));
      });
  }, [dispatch]);

  const hasRolePortal = (roleId) => twoFactorRolesPortal.some((value) => value === roleId);
  const hasRoleApp = (roleId) => twoFactorRolesApp.some((value) => value === roleId);

  const toggleRolePortal = (roleId) => {
    const id = Number(roleId);
    if (hasRolePortal(id)) {
      setTwoFactorRolesPortal((previousRoles) => previousRoles.filter((value) => value !== id));
      return;
    }
    setTwoFactorRolesPortal((previousRoles) => [...previousRoles, id]);
  };

  const toggleRoleApp = (roleId) => {
    const id = Number(roleId);
    if (hasRoleApp(id)) {
      setTwoFactorRolesApp((previousRoles) => previousRoles.filter((value) => value !== id));
      return;
    }
    setTwoFactorRolesApp((previousRoles) => [...previousRoles, id]);
  };

  const validateForm = () => {
    const err = 'organization.organization-settings.security-policy.errors';
    const nextErrors = new Map();
    if (enableTwoFactorPortal && twoFactorRolesPortal.length === 0) {
      nextErrors.set('portalRoles', t(`${err}.portal-roles-required`));
    }
    if (enableTwoFactorApp && twoFactorRolesApp.length === 0) {
      nextErrors.set('appRoles', t(`${err}.app-roles-required`));
    }
    return nextErrors;
  };

  const onSubmit = () => {
    const nextErrors = validateForm();
    setErrors(nextErrors);
    if (nextErrors.size > 0) {
      dispatch(showErrorToast('messages.fix-fields'));
      return;
    }
    setIsSubmitting(true);
    updateMemberOrganization({
      enable_2fa_portal: enableTwoFactorPortal,
      enable_2fa_app: enableTwoFactorApp,
      enable_2fa_roles_portal: twoFactorRolesPortal,
      enable_2fa_roles_app: twoFactorRolesApp,
      allow_2fa_skip_configuration: allow2faSkipConfiguration,
    })
      .then((response) => {
        setIsSubmitting(false);
        const { data } = response;
        dispatch(showSuccessToast('organization.organization-settings.save-success-message'));
        dispatch(setActiveOrganizationData(data));
        dispatch(
          setOrganizations(
            organizations.map((item) => {
              if (item.id === data.id) {
                return data;
              }
              return item;
            }),
          ),
        );
      })
      .catch(() => {
        setIsSubmitting(false);
        dispatch(showErrorToast('organization.organization-settings.save-failure-message'));
      });
  };

  return (
    <App>
      <Fade>
        <Box maxWidth="428px" marginBottom="48px">
          <Subtitle>{t('organization.organization-settings.security-policy.subtitle')}</Subtitle>

          <Typography variant="body2" color="secondary" marginBottom="24px">
            {t('organization.organization-settings.security-policy.text')}
          </Typography>

          <SectionLoader loading={!rolesLoaded}>
            <Fade>
              <Stack spacing={2.5} marginBottom="8px">
                <Paper
                  variant="outlined"
                  data-testid="two-factor-skip-configuration-section"
                  sx={{
                    p: 2.5,
                    borderRadius: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} color="primary.main" marginBottom={2}>
                    {t('organization.organization-settings.security-policy.skip-2fa-section-title')}
                  </Typography>
                  <Typography variant="body2" color="secondary" marginBottom={2}>
                    {t('organization.organization-settings.security-policy.skip-2fa-description')}
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      label={t('organization.organization-settings.security-policy.skip-2fa-enable-label')}
                      control={
                        <Checkbox
                          checked={allow2faSkipConfiguration}
                          onChange={(_, checked) => setAllow2faSkipConfiguration(checked)}
                          inputProps={{ 'data-testid': 'allow-2fa-skip-configuration-checkbox' }}
                        />
                      }
                    />
                  </FormGroup>
                </Paper>

                <Paper
                  variant="outlined"
                  data-testid="two-factor-portal-section"
                  sx={{
                    p: 2.5,
                    borderRadius: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} color="primary.main" marginBottom={2}>
                    {t('organization.organization-settings.security-policy.section-portal-title')}
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      label={t('organization.organization-settings.security-policy.channel-portal-label')}
                      control={
                        <Checkbox
                          checked={enableTwoFactorPortal}
                          onChange={(_, checked) => setEnableTwoFactorPortal(checked)}
                          inputProps={{ 'data-testid': 'two-factor-portal-checkbox' }}
                        />
                      }
                    />
                  </FormGroup>
                  {errors.has('portalChannel') ? (
                    <Typography color="error" variant="body2" data-testid="two-factor-portal-channel-error">
                      {errors.get('portalChannel')}
                    </Typography>
                  ) : null}
                  <Box
                    sx={{
                      mt: 1,
                      pl: 2.5,
                      ml: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="secondary" marginBottom="12px">
                      {t('organization.organization-settings.security-policy.roles-portal-hint')}
                    </Typography>
                    <FormGroup>
                      {roles.map((role) => (
                        <FormControlLabel
                          key={`two-factor-portal-role-${role.id}`}
                          label={t(`name-role-${role.id}`)}
                          disabled={!enableTwoFactorPortal}
                          control={
                            <Checkbox
                              checked={hasRolePortal(Number(role.id))}
                              onChange={() => toggleRolePortal(role.id)}
                              disabled={!enableTwoFactorPortal}
                              inputProps={{ 'data-testid': `role-portal-checkbox-${role.id}` }}
                            />
                          }
                        />
                      ))}
                    </FormGroup>
                    {errors.has('portalRoles') ? (
                      <Typography color="error" variant="body2" data-testid="two-factor-portal-roles-error">
                        {errors.get('portalRoles')}
                      </Typography>
                    ) : null}
                  </Box>
                </Paper>

                <Paper
                  variant="outlined"
                  data-testid="two-factor-app-section"
                  sx={{
                    p: 2.5,
                    borderRadius: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} color="primary.main" marginBottom={2}>
                    {t('organization.organization-settings.security-policy.section-app-title')}
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      label={t('organization.organization-settings.security-policy.channel-app-label')}
                      control={
                        <Checkbox
                          checked={enableTwoFactorApp}
                          onChange={(_, checked) => setEnableTwoFactorApp(checked)}
                          inputProps={{ 'data-testid': 'two-factor-app-checkbox' }}
                        />
                      }
                    />
                  </FormGroup>
                  {errors.has('appChannel') ? (
                    <Typography color="error" variant="body2" data-testid="two-factor-app-channel-error">
                      {errors.get('appChannel')}
                    </Typography>
                  ) : null}
                  <Box
                    sx={{
                      mt: 1,
                      pl: 2.5,
                      ml: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="secondary" marginBottom="12px">
                      {t('organization.organization-settings.security-policy.roles-app-hint')}
                    </Typography>
                    <FormGroup>
                      {roles.map((role) => (
                        <FormControlLabel
                          key={`two-factor-app-role-${role.id}`}
                          label={t(`name-role-${role.id}`)}
                          disabled={!enableTwoFactorApp}
                          control={
                            <Checkbox
                              checked={hasRoleApp(Number(role.id))}
                              onChange={() => toggleRoleApp(role.id)}
                              disabled={!enableTwoFactorApp}
                              inputProps={{ 'data-testid': `role-app-checkbox-${role.id}` }}
                            />
                          }
                        />
                      ))}
                    </FormGroup>
                    {errors.has('appRoles') ? (
                      <Typography color="error" variant="body2" data-testid="two-factor-app-roles-error">
                        {errors.get('appRoles')}
                      </Typography>
                    ) : null}
                  </Box>
                </Paper>
              </Stack>
            </Fade>
          </SectionLoader>
        </Box>
      </Fade>

      <Button variant="outlined" loading={isSubmitting} onClick={onSubmit} data-testid="save-button">
        {t('organization.organization-settings.security-policy.submit-button-text')}
      </Button>
      <Button sx={{ marginLeft: 2 }} variant="outlined" onClick={() => navigate('/')} data-testid="cancel-button">
        {t('organization.organization-settings.security-policy.cancel-button-text')}
      </Button>
    </App>
  );
}

export default OrganizationSecurityPolicy;
